import { useCallback } from 'react'
import { usePuzzlePacks } from 'client/pages/puzzle_packs/hooks/usePuzzlePacks'
import { Role } from 'client/enums/Role'
import useRoleAccess from './useRoleAccess'
import useRoles from './useRoles'

export const usePuzzlePacksForUser = () => {
  const { puzzlePacks } = usePuzzlePacks()
  const { hasRequiredRole } = useRoleAccess()
  const { isSubscriber } = useRoles()

  const showAsPlayable = useCallback((requiredRole: Role) => {
    if (requiredRole === Role.subscriber) {
      // Only subscribers can play subscriber packs
      return isSubscriber
    }
    // Anonymous can play packs that require anonymous and identified,
    // albeit they need to identify to play the latter.
    // Still we want to show those as playable.
    return true
  }, [isSubscriber])

  // Get the playable packs for the user that can be played (purchased/free or accessible after registration, not completed)
  const getPlayablePacksForUser = useCallback(() => {
    return puzzlePacks
      .filter(({
        isPurchased, isFree, isCompleted, minimumRole,
      }) => !isCompleted && (isPurchased || (isFree && showAsPlayable(minimumRole.name))))
      .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime())
      .sort((a, b) => +hasRequiredRole(b.minimumRole.name) - +hasRequiredRole(a.minimumRole.name))
      .sort((a, b) => +b.isNew - +a.isNew)
      .sort((a, b) => +b.isPurchased - +a.isPurchased)
      .sort((a, b) => new Date(b.lastPlayed).getTime() - new Date(a.lastPlayed).getTime())
  }, [puzzlePacks, hasRequiredRole, showAsPlayable])

  // Get the playable packs for the current role that can be played now (purchased/free, not completed)
  const getPlayablePacksForRole = useCallback(() => {
    return getPlayablePacksForUser()
      .filter(({ isPurchased, isFree, minimumRole }) => {
        return isPurchased || (isFree && hasRequiredRole(minimumRole.name))
      })
  }, [getPlayablePacksForUser, hasRequiredRole])

  const getUnplayableFreePacksForUser = useCallback(() => {
    return puzzlePacks
      .filter(({
        isPurchased, isFree, isCompleted, minimumRole,
      }) => (!isPurchased && isFree) && !isCompleted && !showAsPlayable(minimumRole.name))
  }, [puzzlePacks, showAsPlayable])

  return {
    getPlayablePacksForUser,
    getPlayablePacksForRole,
    getUnplayableFreePacksForUser,
  }
}
